/* eslint-disable no-confusing-arrow */
import React, { useEffect, useMemo, useState } from 'react';
import { demeterApi, demeterUserDataApi } from '../../../Apis/Apis';
import {
    Currency,
    DemeterDataFrequency,
    DemeterDataSource,
    DemeterFilterTimeSpan,
    DemeterRegion,
    DemeterSubRegion,
    DemeterTableDefinitionType,
    DemeterUserType,
    ListCommodityMonthlyPricesResponse,
    MarketPriceModel,
    UnitOfMeasure,
} from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserType } from '../../../Redux/Slices/UserSlice';
import useMultipleApis from '../../Apis/Hooks/useMultipleApisHook';
import { ListCommodityPricesResponse } from '../../Apis/Hooks/usePricesApiHook';
import useSymbolsApi from '../../Apis/Hooks/useSymbolsApiHook';
import { ChartDisplayType, IChartDataSeries } from '../../Components/Charts/ChartDefinitions';
import chartService from '../../Components/Charts/ChartService';
import ChartWrapper from '../../Components/Charts/ChartWrapper/ChartWrapper';
import FilterTimeSpans from '../../Components/Charts/FilterTimeSpans/FilterTimeSpans';
import PriceChartRaw from '../../Components/Charts/Price/PriceChartRaw';
import { ChartStudyType } from '../../Components/Charts/Studies/StudiesDefinitions';
import StudiesDropdown from '../../Components/Charts/Studies/StudiesDropdown';
import { IRegionCommoditySelection } from '../../Components/Navigation/Hooks/useRegionCommodityNavigationHook';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';
import { IWithPageElementProps } from '../DashboardCustomization/DashboardCustomizationDefinitions';

export interface IPhysicalPricesChartProps extends IWithPageElementProps {
    title: string;
    regionCommoditySelections: IRegionCommoditySelection[];
    tableDefinitionType: DemeterTableDefinitionType;
    dataFrequency: DemeterDataFrequency;
    isXlsxDownloadAvailable?: boolean;
    displayDecimalPlacesMinimum?: number;
    displayDecimalPlacesMaximum?: number;
    currency?: Currency;
    unitOfMeasure?: UnitOfMeasure;
    testId?: string;
    showPopout?: boolean;
    showOnlyAsPopout?: boolean;
    setShowPopout?: (showPopout: boolean) => void;
}

const availableFilterTimeSpans = [
    DemeterFilterTimeSpan.OneYear,
    DemeterFilterTimeSpan.FiveYears,
    DemeterFilterTimeSpan.TenYears,
    DemeterFilterTimeSpan.TwentyYears,
];

const defaultFilterTimeSpan = DemeterFilterTimeSpan.FiveYears;

const defaultDisplayTypes = [ChartDisplayType.Line];
const singleLineDisplayTypes = [...defaultDisplayTypes, ChartDisplayType.Area, ChartDisplayType.Column, ChartDisplayType.Spline];

const PhysicalPricesChart: React.FC<IPhysicalPricesChartProps> = (props: IPhysicalPricesChartProps) => {
    // Text hooks.
    const [translations, translate] = useLanguage();
    const currentUserType = useApplicationSelector(selectUserType);
    const allowedDownloadUsers: DemeterUserType[] = [DemeterUserType.Administrator, DemeterUserType.Premium, DemeterUserType.BusinessOwner];

    // Api hooks.
    const symbols = useSymbolsApi();
    const [filterTimeSpan, setFilterTimeSpan] = useState<DemeterFilterTimeSpan>(defaultFilterTimeSpan);
    const [physicalPricesLoading, refreshApi, physicalPricesDatas] = useMultipleApis<undefined, ListCommodityPricesResponse>(
        () => {
            if (props.regionCommoditySelections.length === 0) {
                return null;
            }

            if (props.tableDefinitionType === DemeterTableDefinitionType.CommodityGlobalDairyTradeWeeklyPricesTable) {
                return props.regionCommoditySelections.map((x) =>
                    demeterApi.listGlobalDairyTradeWeeklyPrices(x.commodity, x.region as DemeterRegion, props.unitOfMeasure, filterTimeSpan),
                );
            }

            if (props.dataFrequency === DemeterDataFrequency.Monthly) {
                return props.regionCommoditySelections.map((x) => {
                    if (x.region === DemeterRegion.UserData) {
                        return demeterUserDataApi.listUserDataMonthlyPrices(x.extraParameters, props.currency, props.unitOfMeasure, filterTimeSpan);
                    }

                    return demeterApi.listCommodityMonthlyPrices(
                        x.region,
                        x.commodity,
                        x.extraParameters as DemeterDataSource,
                        props.currency,
                        x.subRegion as DemeterSubRegion,
                        props.unitOfMeasure,
                        filterTimeSpan,
                    );
                });
            }

            if (props.dataFrequency === DemeterDataFrequency.Weekly) {
                return props.regionCommoditySelections.map((x) =>
                    demeterApi.listCommodityWeeklyPrices(
                        x.region,
                        x.commodity,
                        x.extraParameters as DemeterDataSource,
                        props.currency,
                        x.subRegion as DemeterSubRegion,
                        props.unitOfMeasure,
                        filterTimeSpan,
                    ),
                );
            }

            return props.regionCommoditySelections.map((x) =>
                demeterApi.listCommoditySpotPrices(
                    x.region,
                    x.commodity,
                    x.extraParameters as DemeterDataSource,
                    props.currency,
                    x.subRegion as DemeterSubRegion,
                    props.unitOfMeasure,
                    filterTimeSpan,
                ),
            );
        },
        { stopAutoExecute: true },
    );

    // Data hooks.
    const [linesSeries, setLinesSeries] = useState<IChartDataSeries[]>([]);
    const [displayType, setDisplayType] = useState<ChartDisplayType>(ChartDisplayType.Line);
    const [studies, setStudies] = useState<ChartStudyType[]>([]);

    useEffect(() => {
        if (props.regionCommoditySelections.length > 0) {
            refreshApi();
        }
        if (props.regionCommoditySelections.length > 1) {
            setStudies([]);
        }
    }, [props.regionCommoditySelections, props.currency, props.unitOfMeasure, filterTimeSpan]);

    useEffect(() => {
        if (!physicalPricesDatas || physicalPricesDatas.length === 0) {
            setLinesSeries([]);
            return;
        }

        const newLinesSeries = physicalPricesDatas.map((data, index) => {
            const selection = props.regionCommoditySelections[index];
            const label = translate(selection?.displayName!);
            const symbol = symbols?.find((x) => x.reutersInstrumentCodePrefix === (data as ListCommodityMonthlyPricesResponse).reutersInstrumentCodePrefix);

            const futuresForwardCurve: MarketPriceModel[] | null | undefined = (data as ListCommodityMonthlyPricesResponse).futuresMarketPricesForwardCurve;
            return {
                label,
                forecastLabel: `${label} ${translations.words.forecast}`,
                data: data!
                    // Remove null value from first index if it exist to stop error from relative strength index study.
                    .rows!.filter((x, rowIndex) => x.value !== null || rowIndex !== 0)
                    .map((row) => ({
                        value: row.value!,
                        asOfDate: new Date(row.asOfDate),
                        isActualValue: row.isActualValue,
                    })),
                forwardCurveLabel: `${formattingService.toDisplayName(symbol)} ${translations.physicalPrices.text.futuresForwardCurve}`,
                forwardCurveData: futuresForwardCurve?.map((curve) => ({
                    value: curve.settlementPrice,
                    asOfDate: new Date(curve.contractYear, curve.contractMonth - 1, 1),
                    isActualValue: false,
                })),
                forecastErrorRangeData: studies.includes(ChartStudyType.ForecastErrorRange)
                    ? (data as ListCommodityMonthlyPricesResponse).forecastErrorRanges
                    : [],
                forecastRangeLabel: `${label} ${translations.words.forecast} ${translations.words.range}`,
                currency: data.currency,
                unitOfMeasure: data.unitOfMeasure,
            };
        });

        setLinesSeries(newLinesSeries);
    }, [physicalPricesDatas, symbols, studies]);

    const dataSourceTag = useMemo(() => physicalPricesDatas?.flatMap((x) => (x.dataSourceTag ?? '').split(', ')) ?? [], [physicalPricesDatas]);
    const hasRestrictedDataSource = chartService.isRestrictedDataSource(...dataSourceTag);
    const oldestAsOfDate = useMemo((): Date | undefined => {
        if (!physicalPricesDatas || !physicalPricesDatas[0] || !physicalPricesDatas[0].oldestAsOfDate) {
            return undefined;
        }

        const date = Math.min(...physicalPricesDatas.map((x) => Date.parse(x.oldestAsOfDate ?? '')));

        return new Date(date);
    }, [physicalPricesDatas]);

    return (
        <ChartWrapper
            name="PhysicalPricesChart"
            customTitle={props.customTitle}
            title={linesSeries.length === 1 && linesSeries[0].label ? linesSeries[0].label : props.title}
            dataSourceTag={dataSourceTag}
            isLoading={physicalPricesLoading && linesSeries.length === 0}
            header={
                <StudiesDropdown
                    includeErrorRange={!!(physicalPricesDatas && (physicalPricesDatas[0] as ListCommodityMonthlyPricesResponse).forecastErrorRanges?.length)}
                    disabled={linesSeries.length > 1}
                    studies={studies}
                    handleStudiesSelected={(selectedStudies) => setStudies(selectedStudies)}
                    track={{ eventCategory: 'PhysicalPrices', eventAction: 'StudiesChanged', eventValue: studies.join(',') }}
                />
            }
            showPopout={props.showPopout}
            setShowPopout={props.setShowPopout}
            maxWidthHeightRatio={props.maxWidthHeightRatio}
            headerOptions={{
                // TODO: We might want this as a permission type added in the backend.
                isCsvDownloadAvailable: !hasRestrictedDataSource && allowedDownloadUsers.includes(currentUserType!),
                isXlsxDownloadAvailable: !hasRestrictedDataSource && props.isXlsxDownloadAvailable && allowedDownloadUsers.includes(currentUserType!),
                chartDisplayTypes: linesSeries.length <= 1 ? singleLineDisplayTypes : defaultDisplayTypes,
                handleChartDisplayTypeChange: (newDisplayType: ChartDisplayType) => {
                    setDisplayType(newDisplayType);
                },
                handleMoreOptionsSelection: props.handleMoreOptionsSelection,
                showOnlyAsPopout: props.showOnlyAsPopout,
                isInDashboardEditMode: props.isInDashboardEditMode,
                maximizeMinimizeButton: props.maximizeMinimizeButton,
            }}
            footer={
                <FilterTimeSpans
                    name="PhysicalPricesChart"
                    filterTimeSpanOptions={availableFilterTimeSpans}
                    filterTimeSpan={filterTimeSpan}
                    oldestAsOfDate={oldestAsOfDate}
                    handleTimeSpanSelected={(timeSpan) => setFilterTimeSpan(timeSpan)}
                    track={{ eventCategory: 'PhysicalPrices', eventAction: 'FilterTimeSpanChanged' }}
                />
            }
            testId={props.testId}
            dragIndicatorIcon={props.dragIndicatorIcon}
            track={{ eventCategory: 'Futures', eventAction: 'PhysicalPricesChart' }}
        >
            <PriceChartRaw
                displayType={displayType}
                linesSeries={linesSeries}
                dataFrequency={props.dataFrequency}
                currency={props.currency}
                unitOfMeasure={props.unitOfMeasure}
                filterTimeSpan={filterTimeSpan}
                displayDecimalPlacesMinimum={props.displayDecimalPlacesMinimum}
                displayDecimalPlacesMaximum={props.displayDecimalPlacesMaximum}
                studies={studies}
                disableTooltip={hasRestrictedDataSource}
            />
        </ChartWrapper>
    );
};

export default PhysicalPricesChart;
